import React, { useState, useEffect, useRef } from 'react';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import Button from 'chameleon/ui-stack/ui/base/button';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import dayjs from 'dayjs';
import './styles.scss';
import Tooltip from 'chameleon/ui-stack/ui/base/tooltip';
import PropTypes from 'prop-types';
import WrappedDatePicker from 'chameleon-src/components/assign/AssignManageForm/options/TimeOptions/DateField/WrappedDataPicker';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import ManualTimeSelection from 'chameleon-src/components/assign/AssignManageForm/options/TimeOptions/DateField/ManualTimeSelection';

export const DatePickerPopup = ({
  onChange,
  initialValue,
  minDate,
  maxDate,
  disabled,
  errorMessage,
  tooltip,
  timezone,
  showReset,
}) => {
  const popoverRef = useRef();
  const [isOpen, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [errMessage, setError] = useState(errorMessage);
  const [isChanged, setChanged] = useState(false);
  const [isSaved, setSaved] = useState(false);
  const [yearDayMonthLabel, setYearDayMonthLabel] = useState(null);
  const [hoursMinutesLabel, setHoursMinutesLabel] = useState(null);

  useEffect(() => {
    const value = isChanged ? selectedDate : selectedDate || initialValue;
    if (value) {
      const date = dayjs.isDayjs(value) ? value : dayjs(value).tz(timezone);
      setYearDayMonthLabel(date.format('MM/DD'));
      setHoursMinutesLabel(date.format('hh:mm A'));
    } else {
      setYearDayMonthLabel('-');
      setHoursMinutesLabel('');
    }
  }, [selectedDate, initialValue, isChanged]);

  const dayJSTime = initialValue ? dayjs(initialValue).tz(timezone) : null;
  const dayJSTimeMS = dayJSTime
    ? Date.parse(dayJSTime.format('MM/DD/YYYY hh:mm A'))
    : false;

  const tooltipTargetId = 'DatePickerPopup-tooltip';
  const onReset = () => {
    if (!isOpen) {
      popoverRef.current.open();
    }
    setSelectedDate(null);
    setChanged(true);
    setSaved(false);
  };

  const onManualTimeSet = ({ hours, minutes }) => {
    let value = selectedDate || initialValue;
    if (value) {
      let date = dayjs(value);
      date = date.set('hour', hours);
      date = date.set('minute', minutes);

      setSelectedDate(date);
      setChanged(true);
      setSaved(false);
    }
  };

  return (
    <Tooltip target={tooltipTargetId} description={tooltip}>
      <PopOver
        ref={popoverRef}
        disabled={disabled}
        className="datepicker_popup_container"
        contentClassName="datepicker_popup flex flex-col items-center border border-gray-500"
        isNarrow
        size="medium"
        onClose={() => {
          setChanged(false);
          setError(null);
          !isSaved &&
            setSelectedDate(
              initialValue ? dayjs(initialValue).tz(timezone) : null,
            );
        }}
        onToggle={setOpen}
        accessibility={{
          'aria-label': 'Date Picker',
        }}
        trigger={
          <div
            id={tooltipTargetId}
            className={`w-60 flex items-center justify-start
            border cursor-pointer p-2 rounded-3xl
            ${!isOpen ? 'border-gray-400' : 'border-black'}`}
          >
            <Icon name="calendar" />
            {hoursMinutesLabel ? (
              <div className="pt-1">
                <span
                  data-test-id={DATA_TEST_IDS.DATE_YEARS_MONTH_LABEL}
                  className="pl-2"
                >
                  {yearDayMonthLabel}
                </span>
                <span className="pl-2">
                  <ManualTimeSelection
                    value={hoursMinutesLabel}
                    setManualTime={onManualTimeSet}
                  />
                </span>
              </div>
            ) : (
              <span className="pl-4 flex-1">&#8211;</span>
            )}
            {showReset && (
              <div onClick={onReset} className="flex flex-1 justify-end">
                <Icon
                  id="remove_circle"
                  width="14"
                  height="14"
                  className="text-gray-500"
                />
              </div>
            )}
          </div>
        }
      >
        <WrappedDatePicker
          timezone={timezone}
          meta={{ error: '', touched: true }}
          input={{
            onChange: (date) => {
              setSelectedDate(date);
              setError(null);
              setChanged(true);
              setSaved(false);
            },
            value: '',
          }}
          selectedValue={
            isChanged
              ? selectedDate
                ? selectedDate.format('MM/DD/YYYY hh:mm A')
                : null
              : initialValue
                ? dayJSTimeMS
                : false
          }
          minDate={minDate}
          maxDate={maxDate}
          overridingProps={{
            class: '',
          }}
          inline
          isAssignmentTable
          onPastSelect={() => {
            setSelectedDate(dayjs().tz(timezone).utcOffset(0, true));
            setChanged(true);
            setSaved(false);
          }}
        />
        <div
          className="datepicker__current-time border-0 border-b border-gray-500 text-14 font-medium"
          role="button"
          tabIndex="0"
          onClick={() => {
            setSelectedDate(dayjs().tz(timezone).utcOffset(0, true));
            setChanged(true);
            setSaved(false);
          }}
        >
          Select Current Date and Time
        </div>
        {errMessage && (
          <div className="error_message bg-red-100 text-red-600 py-4 px-6 border-0 border-b border-gray-500 break-words">
            {errMessage}
          </div>
        )}
        <div className="w-full flex justify-end p-6">
          <Button isNaked onClick={() => popoverRef.current.close()}>
            Cancel
          </Button>
          <Button
            disabled={
              !isChanged ||
              (selectedDate && dayJSTimeMS === selectedDate.valueOf())
            }
            className="ml-10"
            onClick={() => {
              onChange(isChanged ? selectedDate : dayJSTime)
                .then(() => {
                  setError(null);
                  setChanged(true);
                  setSaved(true);
                  popoverRef.current.close();
                })
                .catch((err) => {
                  setError(err);
                  setSelectedDate(
                    initialValue ? dayjs(initialValue).tz(timezone) : null,
                  );
                  setSaved(false);
                });
            }}
            isPrimary
          >
            Update
          </Button>
        </div>
      </PopOver>
    </Tooltip>
  );
};

DatePickerPopup.displayName = 'DatePickerPopup';
DatePickerPopup.defaultProps = {
  tooltip: 'Update date & time',
  showReset: true,
};
DatePickerPopup.propTypes = {
  onChange: PropTypes.func,
  initialValue: PropTypes.string,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  tooltip: PropTypes.string,
};
